import { cloneElement, isValidElement, ReactElement, useEffect, useState } from 'react';
import { Collapse, Fade, SxProps, Theme } from '@mui/material';

// Highlight the item green for HOLD_MS, then fade + collapse it away over FADE/COLLAPSE_MS.
const HOLD_MS = 500;
const FADE_MS = 320;
const COLLAPSE_MS = 320;

// Total time from an item leaving the list to its exit animation finishing (fade overlaps collapse).
// Consumers use this to hold off on the empty/celebration UI until the last item has animated out.
export const FEED_ITEM_EXIT_MS = HOLD_MS + COLLAPSE_MS;

type Phase = 'open' | 'hold' | 'collapsing';

interface FeedItemTransitionProps {
    /** Supplied by TransitionGroup — flips to false once the item leaves the list. */
    in?: boolean;
    /** Supplied by TransitionGroup — called when the exit transition finishes so it can unmount. */
    onExited?: () => void;
    orientation?: 'vertical' | 'horizontal';
    /**
     * Applied to the Collapse. Feed rows pass a horizontal bleed so the row's completing-green spill
     * isn't cropped by the Collapse's overflow:hidden during the exit.
     */
    sx?: SxProps<Theme>;
    children: ReactElement<{ isCompleting?: boolean }>;
}

/**
 * Exit animation for a member-task feed item: when the item leaves the query data it flashes green
 * (~0.5s), then fades + collapses away. Rendered as a child of react-transition-group's
 * `TransitionGroup`, which drives `in`/`onExited`. It injects `isCompleting` into its child so the
 * child's own shell tints its surface green (the shells own opaque backgrounds, so the tint can't be
 * painted from the wrapper). Enter is disabled (`enter={false}`) so items appear at full size the
 * instant they arrive — a later-added card shouldn't animate its width/height in (and the carousel
 * measures card widths, so an animating width would throw it off). Only the exit is choreographed.
 */
export default function FeedItemTransition({
    in: inProp = true,
    onExited,
    orientation = 'vertical',
    sx,
    children,
}: Readonly<FeedItemTransitionProps>) {
    const [phase, setPhase] = useState<Phase>('open');

    useEffect(() => {
        if (inProp) {
            setPhase('open');
            return undefined;
        }
        setPhase('hold');
        const timer = setTimeout(() => setPhase('collapsing'), HOLD_MS);
        return () => clearTimeout(timer);
    }, [inProp]);

    const isCompleting = phase !== 'open';
    const open = phase !== 'collapsing';
    // Carousel cards stretch to the tallest card via the scroller's `alignItems: stretch`. Let the
    // Collapse root stretch as a flex item (align-self: stretch) — do NOT set height:100% on it, since
    // with no ancestor of definite height that resolves to content height and suppresses the stretch.
    // Once the root is stretched, height:100% on its inner wrappers + our div carries the height to the
    // card. Horizontal only; vertical rows size to content.
    const horizontal = orientation === 'horizontal';
    const innerStyle = horizontal ? { height: '100%', display: 'flex' } : undefined;

    return (
        <Collapse
            in={open}
            enter={false}
            orientation={orientation}
            timeout={COLLAPSE_MS}
            onExited={onExited}
            sx={[
                ...(horizontal
                    ? [
                          {
                              alignSelf: 'stretch',
                              '& .MuiCollapse-wrapper, & .MuiCollapse-wrapperInner': { height: '100%' },
                          },
                      ]
                    : []),
                ...(Array.isArray(sx) ? sx : [sx]),
            ]}>
            <Fade in={open} enter={false} timeout={FADE_MS}>
                <div style={innerStyle}>
                    {isValidElement(children) ? cloneElement(children, { isCompleting }) : children}
                </div>
            </Fade>
        </Collapse>
    );
}
